import {
  ACTIVITY_STEPS,
  INCURSION_SITES,
  BASTION,
  CALENDAR,
  MIN_TOTAL_ACTIVITY_BP,
  LAND_COST,
  LAND_START_STORAGE_PER_PLAYER,
  START_STORAGE_PER_PLAYER,
  STRUCTURES,
  res,
  sectorsForPlayers,
  type HeroClass,
  type Resources,
  type StructureType,
} from '@somnhold/content';
import { DEFAULT_PROFILE, structureMaxHp } from '../battle/create.ts';
import { buildTerrain, cellId } from '../grid.ts';
import { hexKey, parseHexKey } from '../hex.ts';
import { createCampaignLayout, openSectors } from '../map.ts';
import { createLandMap, footprint, isBig } from '../land.ts';
import { wallCells } from '../walls.ts';
import { twoHanded } from './items.ts';
import { owedRelics } from './unsleeping.ts';
import { mulBp, sqrtBp } from '../math/fixed.ts';
import { seedRng } from '../math/rng.ts';
import { T_GATE, T_INTERIOR, type MapDef, type PlayerId } from '../types.ts';
import type { Built, CampaignEvent, CampaignEventKind, CampaignState, CellState, Ms, PlayerState } from './types.ts';

export const PLAYER_COLORS = ['#7fe3d6', '#9fc8ff', '#b3f59a', '#ffb38a', '#e3a8ff', '#ffe08a', '#ff9ab0', '#c8d0dc'];

export interface NewPlayer {
  id: PlayerId;
  displayName: string;
  heroClass: HeroClass;
}

export interface NewCampaign {
  id: string;
  seed: number;
  createdAt: Ms;
  timezone: string;
  waveHours?: [number, number, number];
  depth?: 'light' | 'deep';
  lang?: 'es' | 'en';
  players: NewPlayer[];
  /** The bastion from scratch (docs/CONSTRUCCION.md): terraced land and the keep alone, no ring. */
  land?: boolean;
}

/** The name a player's hero goes by on the field: its own, or else its player's. */
export const heroName = (p: Pick<PlayerState, 'displayName' | 'hero'>): string => p.hero.name || p.displayName;

export const newPlayer = (p: NewPlayer, at: Ms, index: number): PlayerState => ({
  id: p.id,
  displayName: p.displayName,
  color: PLAYER_COLORS[index % PLAYER_COLORS.length] as string,
  hero: { heroClass: p.heroClass, level: 1, xp: 0, skillPoints: 0, skills: {}, gear: {}, bag: [], relics: [] },
  joinedAt: at,
  lastActiveAt: at,
  left: false,
  adoptedSectors: [],
  incursionsToday: 0,
  incursionDay: -1,
  ghostPost: null,
  ghostProfile: DEFAULT_PROFILE(p.heroClass),
  fortified: null,
  enchanted: [],
});

export const createCampaign = (c: NewCampaign): CampaignState => {
  const layout = c.land ? createLandMap(c.seed) : createCampaignLayout(c.seed);
  const players = c.players
    .slice()
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
    .map((p, i) => newPlayer(p, c.createdAt, i));
  const n = players.length;
  const start = c.land ? LAND_START_STORAGE_PER_PLAYER : START_STORAGE_PER_PLAYER;
  const storage = res({
    wood: start.wood * n,
    stone: start.stone * n,
    crystal: start.crystal * n,
  });
  const s: CampaignState = {
    v: 1,
    id: c.id,
    titan: 'turtle',
    depth: c.depth ?? 'light',
    seed: c.seed >>> 0,
    createdAt: c.createdAt,
    timezone: c.timezone,
    waveHours: c.waveHours ?? CALENDAR.defaultWaveHours,
    lang: c.lang ?? 'es',
    mareaIndex: 0,
    draftFrom: 0,
    waveIndex: 0,
    mareaLost: false,
    lossPenalty: false,
    consecutiveLosses: 0,
    status: 'active',
    players,
    map: openSectors(layout, sectorsForPlayers(n)),
    cells: {},
    keep: { hp: 0, maxHp: 0 },
    storage,
    nextId: 1,
    rng: seedRng(c.seed ^ 0xca4a),
    notes: [],
    events: [],
    waves: [],
    board: [],
    clock: c.createdAt,
    version: 1,
    bossesDefeated: [],
  };
  players.forEach((p, i) => {
    // Terraced land has no ways in fixed (each wave opens its own fissures): ghosts go where the pressure is.
    p.ghostPost = c.land ? { anywhere: true } : { sector: i % s.map.sectors.length };
    p.adoptedSectors = [i % s.map.sectors.length];
  });
  const maxHp = keepMaxHp(s, c.createdAt);
  s.keep = { hp: maxHp, maxHp };
  ensureRampart(s);
  return s;
};

/**
 * The wall as structures: one run on every wall cell (the ring and the districts' edges) that is not an entrance, so nightmares can
 * break it and players repair it. Idempotent; also brings older campaigns up to date when they load.
 */
export const ensureRampart = (s: CampaignState): boolean => {
  // Terraced land has no ring: the group raises its own walls.
  if (s.map.land) return false;
  const gates = new Set(s.map.sectors.map((x) => hexKey(x.gate)));
  let added = false;
  for (const h of wallCells(s.map)) {
    const k = hexKey(h);
    if (gates.has(k) || s.cells[k]) continue;
    s.cells[k] = {
      built: { structure: 'rampart', level: 1, material: 1, hp: structureMaxHp('rampart', 1, 1, false), charges: 0, builtBy: '', enchant: null, fortifiedUntil: 0 },
      job: null,
    };
    added = true;
  }
  return added;
};

/**
 * Brings a stored campaign up to date when it loads: the wall runs, and the iron (era II) every older
 * store and job lacks. Returns whether anything changed.
 */
export const upgradeState = (s: CampaignState): boolean => {
  let changed = ensureRampart(s);
  // A campaign under way when the general started drafting: from its next Marea, not in the middle of one.
  if (s.draftFrom === undefined) {
    s.draftFrom = s.mareaIndex + 1;
    changed = true;
  }
  // Incursion levels: the places already won at level I, from the log (they open level II).
  if (s.explored && !s.explored.won) {
    const won = new Set<number>();
    for (const e of s.events) {
      const d = e.data as { node?: number; won?: boolean } | undefined;
      if (e.kind === 'incursion' && d?.won && typeof d.node === 'number' && !(d as { tier?: number }).tier) won.add(d.node);
    }
    // The log keeps only the latest events: past the first Marea, every open place of the inner rings counts.
    if (s.mareaIndex >= 1) for (const id of s.explored.open) if ((INCURSION_SITES.find((x) => x.id === id)?.ring ?? 3) < 3) won.add(id);
    s.explored.won = [...won].sort((a, b) => a - b);
    changed = true;
  }
  const fill = (r: Partial<Resources> | undefined): void => {
    if (r && r.iron === undefined) {
      r.iron = 0;
      changed = true;
    }
  };
  fill(s.storage);
  for (const key of Object.keys(s.cells)) {
    const j = s.cells[key]?.job;
    if (j) {
      fill(j.cost);
      fill(j.paid);
    }
  }
  // A unique or older weapon is two-handed when its class's usual one is: what was held beside it goes to the bag.
  for (const p of s.players)
    for (const h of [p.hero, ...(p.bench ?? []).map((b) => b.hero)])
      if (h.gear.offhand && twoHanded(h)) {
        h.bag.push(h.gear.offhand);
        delete h.gear.offhand;
        changed = true;
      }
  for (const n of s.notes) fill(n.refund);
  for (const b of s.board) {
    fill(b.want);
    fill(b.got);
  }
  if (owedRelics(s)) changed = true;
  if (restoreTurns(s)) changed = true;
  return changed;
};

/**
 * Three-cell buildings finished before their turn was kept fell back to turn 0, drawn and fought over cells that
 * weren't theirs. Their parts still mark the cells they took: the turn is the one whose footprint matches.
 */
const restoreTurns = (s: CampaignState): boolean => {
  if (!s.map.land) return false;
  const parts = new Map<string, string[]>();
  for (const key of Object.keys(s.cells).sort()) {
    const of = s.cells[key]?.part;
    if (of) parts.set(of, [...(parts.get(of) ?? []), key]);
  }
  let changed = false;
  for (const [key, mine] of parts) {
    const b = s.cells[key]?.built;
    if (!b || b.turn !== undefined || !isBig(b.structure)) continue;
    const h = parseHexKey(key);
    for (let turn = 1; turn < 6; turn++) {
      const cells = footprint(b.structure, h.q, h.r, turn).slice(1).map(hexKey);
      if (cells.length === mine.length && cells.every((k) => mine.includes(k))) {
        b.turn = turn;
        changed = true;
        break;
      }
    }
  }
  return changed;
};

export const addGate = (s: CampaignState, q: number, r: number): void => {
  const built: Built = {
    structure: 'gate',
    level: 1,
    material: 1,
    hp: structureMaxHp('gate', 1, 1, false),
    charges: 0,
    builtBy: '',
    enchant: null,
    fortifiedUntil: 0,
  };
  s.cells[hexKey({ q, r })] = { built, job: null };
};

/** Full layout (all 5 slots) is derived from the seed, so sectors can open later without moving anything. */
export const campaignLayout = (s: CampaignState): MapDef => createCampaignLayout(s.seed);

/** Activity weight of a player at time `now` (SPEC §9). */
export const activityWeight = (p: PlayerState, now: Ms): number => {
  if (p.left) return 0;
  const idle = now - p.lastActiveAt;
  for (const step of ACTIVITY_STEPS) if (idle <= step.withinMs) return step.weightBp;
  return 0;
};

export const totalActivity = (s: CampaignState, now: Ms): number => {
  let sum = 0;
  for (const p of s.players) sum += activityWeight(p, now);
  return sum < MIN_TOTAL_ACTIVITY_BP ? MIN_TOTAL_ACTIVITY_BP : sum;
};

/** Keep HP = 5000 × sqrt(total activity). */
export const keepMaxHp = (s: CampaignState, now: Ms): number => mulBp(BASTION.keepHpBase, sqrtBp(totalActivity(s, now)));

export const findPlayer = (s: CampaignState, id: PlayerId): PlayerState | undefined => s.players.find((p) => p.id === id);

/**
 * A player leaves for good: their hero stays on the wall as a fading guardian (40 %, SPEC §9) so the
 * group isn't left with an open sector, and they no longer count as playing.
 */
export const leaveCampaign = (s: CampaignState, pid: PlayerId, now: Ms): boolean => {
  const p = findPlayer(s, pid);
  if (!p || p.left) return false;
  p.left = true;
  s.events.push({ id: s.nextId++, at: now, kind: 'left', pid });
  s.version++;
  return true;
};

export const event = (s: CampaignState, at: Ms, kind: CampaignEventKind, extra: Omit<CampaignEvent, 'id' | 'at' | 'kind'> = {}): void => {
  s.events.push({ id: s.nextId++, at, kind, ...extra });
  if (s.events.length > 400) s.events.splice(0, s.events.length - 400);
};

const TERRAIN_CACHE = new WeakMap<MapDef, number[]>();
const terrainOf = (map: MapDef): number[] => {
  let t = TERRAIN_CACHE.get(map);
  if (!t) {
    t = buildTerrain(map);
    TERRAIN_CACHE.set(map, t);
  }
  return t;
};

export const isBuildableCell = (s: CampaignState, q: number, r: number): boolean => {
  const id = cellId(s.map.radius, q, r);
  return id >= 0 && terrainOf(s.map)[id] === T_INTERIOR;
};

/** Gate cells of open sectors take only a gate (on terraced land, any cell open to building). */
export const isGateCell = (s: CampaignState, q: number, r: number): boolean => {
  // On terraced land a gate goes wherever anything else could.
  if (s.map.land) return isBuildableCell(s, q, r);
  const id = cellId(s.map.radius, q, r);
  return id >= 0 && terrainOf(s.map)[id] === T_GATE;
};

export const cellOf = (s: CampaignState, q: number, r: number): CellState | undefined => s.cells[hexKey({ q, r })];

export const builtMaxHp = (b: Built, now: Ms): number =>
  STRUCTURES[b.structure].hp > 0 ? structureMaxHp(b.structure, b.level, b.material, b.fortifiedUntil > now) : 0;

export const maxCharges = (b: Built): number => STRUCTURES[b.structure].charges ?? 0;

/** Keys of all cells in deterministic order (by cell id). */
export const sortedCellKeys = (s: CampaignState): string[] => {
  const R = s.map.radius;
  // Parse each key once, not on every comparison (the sort is stable, so ties keep their order).
  return Object.keys(s.cells)
    .map((k) => {
      const h = parseHexKey(k);
      return { k, id: cellId(R, h.q, h.r) };
    })
    .sort((a, b) => a.id - b.id)
    .map((x) => x.k);
};

export const addRes = (a: Resources, b: Partial<Resources>, sign = 1): void => {
  a.wood += sign * (b.wood ?? 0);
  a.stone += sign * (b.stone ?? 0);
  a.crystal += sign * (b.crystal ?? 0);
  a.dust += sign * (b.dust ?? 0);
  if (b.iron) a.iron = (a.iron ?? 0) + sign * b.iron;
};

export const hasRes = (a: Resources, b: Partial<Resources>): boolean =>
  a.wood >= (b.wood ?? 0) && a.stone >= (b.stone ?? 0) && a.crystal >= (b.crystal ?? 0) && a.dust >= (b.dust ?? 0) && (a.iron ?? 0) >= (b.iron ?? 0);

export const scaleRes = (a: Resources, bp: number): Resources =>
  res({ wood: mulBp(a.wood, bp), stone: mulBp(a.stone, bp), crystal: mulBp(a.crystal, bp), dust: mulBp(a.dust, bp), iron: mulBp(a.iron ?? 0, bp) });

export const sumRes = (a: Partial<Resources>): number => (a.wood ?? 0) + (a.stone ?? 0) + (a.crystal ?? 0) + (a.dust ?? 0) + (a.iron ?? 0);

export const isNonNegRes = (a: Partial<Resources>): boolean =>
  [a.wood, a.stone, a.crystal, a.dust, a.iron].every((v) => v === undefined || (Number.isInteger(v) && v >= 0));

export { hexKey };

/** What a structure costs at level 1 in this bastion (terraced land prices some otherwise, LAND_COST). */
export const baseCost = (s: CampaignState, type: StructureType): Resources => (s.map.land ? LAND_COST[type] : undefined) ?? STRUCTURES[type].cost;
